import {
  formatMessageDateTime,
  isMessageRead,
  messagePreview,
  messageStatusLabel,
  type PlayerMessage,
} from '@/domain/messages';
import { cn } from '@/lib/cn';
import { Icon } from '../common/icon';

export interface MessageListRowProps {
  message: PlayerMessage;
  onSelect?: (message: PlayerMessage) => void;
  className?: string;
}

/**
 * Account inbox list row (Figma MessageListRow). Status=New shows unread dot
 * and primary status; Status=Read is muted.
 */
export function MessageListRow({ message, onSelect, className }: MessageListRowProps) {
  const read = isMessageRead(message);
  const status = messageStatusLabel(message);
  const preview = messagePreview(message);
  const date = formatMessageDateTime(message.Timestamp);

  return (
    <button
      type="button"
      onClick={() => onSelect?.(message)}
      className={cn(
        'flex w-full items-center justify-center gap-3 rounded-md bg-bg-surface',
        'px-4 py-3.5 text-left transition-colors hover:bg-bg-elevated',
        'focus-visible:ring-1 focus-visible:ring-border-focus focus-visible:outline-none',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'size-2 shrink-0 rounded-full',
          read ? 'bg-transparent' : 'bg-action-primary',
        )}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden">
        <p className="truncate text-[14px] leading-[18px] font-bold text-text-primary">
          {message.Title || 'Message'}
        </p>
        {preview ? (
          <p className="truncate text-[12px] leading-4 text-text-secondary">{preview}</p>
        ) : null}
        {date ? (
          <p className="truncate text-[11px] leading-[14px] text-text-secondary">{date}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span
          className={cn(
            'rounded-full bg-bg-elevated px-2 py-0.5 text-[11px] font-bold whitespace-nowrap',
            read ? 'text-text-secondary' : 'text-action-primary',
          )}
        >
          {status}
        </span>
        <Icon name="arrow-right" size={16} className="shrink-0 text-icon-muted" />
      </div>
    </button>
  );
}
